import { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
    clearErrors,
    deleteOrder,
    getAllOrders,
} from '../../actions/orderAction';
import { DELETE_ORDER_RESET } from '../../constants/orderConstants';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

/* ================================================= */

const OrderTable = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const [selectedRows, setSelectedRows] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { orders, error } = useSelector((state) => state.allOrders);
    const { loading, isDeleted, error: deleteError } = useSelector(
        (state) => state.order
    );

    /* ---------------- effects ---------------- */

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: 'error' });
            dispatch(clearErrors());
        }

        if (deleteError) {
            enqueueSnackbar(deleteError, { variant: 'error' });
            dispatch(clearErrors());
        }

        if (isDeleted) {
            enqueueSnackbar('Deleted Successfully', {
                variant: 'success',
            });
            dispatch({ type: DELETE_ORDER_RESET });
        }

        dispatch(getAllOrders());
    }, [dispatch, error, deleteError, isDeleted, enqueueSnackbar]);

    /* ---------------- handlers ---------------- */

    const deleteOrderHandler = (id) => dispatch(deleteOrder(id));

    // open confirmation
    const bulkDeleteHandler = () => {
        setConfirmOpen(true);
    };

    // confirmed delete
    const confirmDelete = () => {
        selectedRows.forEach((id) => dispatch(deleteOrder(id)));
        setSelectedRows([]);
        setConfirmOpen(false);
    };

    /* ---------------- chip ---------------- */

    const StatusChip = ({ label, type }) => {
        const map = {
            success:
                'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
            warning:
                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
            danger:
                'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
        };

        return (
            <span
                className={`px-3 py-1 text-xs rounded-full font-semibold ${map[type]}`}
            >
                {label}
            </span>
        );
    };

    /* ---------------- rows ---------------- */

    const rows = useMemo(
        () =>
            orders
                ?.map((order) => ({
                    id: order._id,
                    itemsQty: order.orderItems.length,
                    amount: order.totalPrice,
                    orderOn: new Date(order.createdAt).toLocaleString(),
                    status: order.orderStatus,
                    paymentStatus: order.paymentInfo?.status || 'unknown',
                }))
                .sort((a, b) => b.orderOn - a.orderOn) // 🔥 newest first
            || [],
        [orders]
    );

    /* ---------------- search ---------------- */

    const filteredRows = useMemo(() => {
        if (!searchText) return rows;

        return rows.filter((row) =>
            Object.values(row)
                .join(' ')
                .toLowerCase()
                .includes(searchText.toLowerCase())
        );
    }, [rows, searchText]);

    /* ---------------- columns ---------------- */

    const columns = [
        { field: 'id', headerName: 'Order ID', flex: 1.2 },

        {
            field: 'status',
            headerName: 'Status',
            flex: 0.4,
            renderCell: (p) => (
                <StatusChip
                    label={p.value}
                    type={
                        p.value === 'Delivered'
                            ? 'success'
                            : 'warning'
                    }
                />
            ),
        },

        {
            field: 'paymentStatus',
            headerName: 'Payment',
            flex: 0.4,
            renderCell: (params) => {
                const s = params.value;

                if (s === 'success')
                    return (
                        <StatusChip label="Success" type="success" />
                    );

                if (s === 'pending')
                    return (
                        <StatusChip label="Pending" type="warning" />
                    );

                return (
                    <StatusChip label="Failed" type="danger" />
                );
            },
        },

        { field: 'itemsQty', headerName: 'Items', flex: 0.2 },

        {
            field: 'amount',
            headerName: 'Amount',
            flex: 0.4,
            renderCell: (p) => (
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                    ₹{p.value.toLocaleString()}
                </span>
            ),
        },

        { field: 'orderOn', headerName: 'Ordered On', flex: 0.6 },

        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            renderCell: (params) => (
                <Actions
                    editRoute="order"
                    deleteHandler={deleteOrderHandler}
                    id={params.row.id}
                />
            ),
        },
    ];

    /* ================================================= */

    return (
        <>
            <MetaData title="Orders" />
            {loading && <BackdropLoader />}

            <div className="p-6 min-h-screen from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">

                {/* top bar */}
                <div className="flex justify-between items-center mb-5 gap-4 flex-wrap">

                    {/* search */}
                    <div className="relative">
                        <SearchIcon className="absolute left-3 top-2.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search orders..."
                            value={searchText}
                            onChange={(e) =>
                                setSearchText(e.target.value)
                            }
                            className="pl-10 pr-4 py-2 rounded-lg border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 shadow-sm focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    {/* bulk delete */}
                    {selectedRows.length > 0 && (
                        <button
                            onClick={bulkDeleteHandler}
                            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow"
                        >
                            <DeleteOutlineIcon fontSize="small" />
                            Delete Selected ({selectedRows.length})
                        </button>
                    )}
                </div>

                {/* table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="backdrop-blur-md bg-white/80 dark:bg-gray-900/70 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800"
                >
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        checkboxSelection
                        selectionModel={selectedRows}
                        onSelectionModelChange={(ids) =>
                            setSelectedRows(ids)
                        }
                        autoHeight
                        pageSizeOptions={[10, 25, 50]}
                        sx={{
                            border: 'none',
                            backgroundColor:
                                theme.palette.background.paper,
                            color: theme.palette.text.primary,

                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor:
                                    theme.palette.background.default,
                                fontWeight: 700,
                            },

                            '& .MuiDataGrid-row:hover': {
                                backgroundColor:
                                    theme.palette.action.hover,
                            },
                        }}
                    />
                </motion.div>
            </div>

            {/* ================= CONFIRM DIALOG ================= */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
            >
                <DialogTitle className="font-semibold">
                    Delete Orders?
                </DialogTitle>

                <DialogContent>
                    You are about to delete{' '}
                    <span className="font-bold text-red-600">
                        {selectedRows.length}
                    </span>{' '}
                    orders permanently.
                    <br />
                    This action cannot be undone.
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        variant="outlined"
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OrderTable;
